/**
 * The rules about API keys the dashboard needs as well as the API (task
 * 437): the limits, the lifetimes the form offers, the label's bounds and a
 * key's state. Browser-safe; the format itself, which needs `node:crypto`,
 * is `@headerhawk/core/api-keys`.
 */
/** A tenant's live (not revoked, not expired) keys at most (task 437). */
export const MAX_LIVE_API_KEYS_PER_TENANT = 20;
/** The lifetimes the form offers; the API accepts exactly these. */
export const API_KEY_EXPIRY_DAYS = [30, 90, 180, 365];
export const DEFAULT_API_KEY_EXPIRY_DAYS = 90;
/** A key's label, as the customer typed it, after trimming. */
export const MAX_API_KEY_NAME_LENGTH = 80;
export const API_KEY_STATES = [
    "live",
    "expiring",
    "expired",
    "suspended",
    "revoked",
];
/** A live key is "expiring" in its last 14 days. */
export const API_KEY_EXPIRING_WITHIN_DAYS = 14;
export function apiKeyState(key, now = new Date()) {
    if (key.revokedAt)
        return "revoked";
    if (key.expiresAt.getTime() <= now.getTime())
        return "expired";
    if (key.suspendedAt)
        return "suspended";
    const soon = now.getTime() + API_KEY_EXPIRING_WITHIN_DAYS * 86_400_000;
    return key.expiresAt.getTime() <= soon ? "expiring" : "live";
}
/** C0 controls and DEL: a line break or a tab in a label is a paste accident. */
function hasControlCharacter(value) {
    for (const char of value) {
        const code = char.charCodeAt(0);
        if (code < 0x20 || code === 0x7f)
            return true;
    }
    return false;
}
/** A key's label: 1 to 80 characters after trimming, no control characters. */
export function parseApiKeyName(value) {
    if (typeof value !== "string")
        return { ok: false, reason: "missing" };
    const name = value.trim();
    if (name.length === 0)
        return { ok: false, reason: "missing" };
    // Code points, as Postgres's `char_length` and JSON Schema's `maxLength`
    // count them, so an emoji is one character here as it is there.
    if ([...name].length > MAX_API_KEY_NAME_LENGTH) {
        return { ok: false, reason: "too_long" };
    }
    if (hasControlCharacter(name)) {
        return { ok: false, reason: "control_characters" };
    }
    return { ok: true, name };
}
export function isApiKeyExpiryDays(value) {
    return (typeof value === "number" &&
        API_KEY_EXPIRY_DAYS.includes(value));
}
/**
 * Requests a minute one API key may make, to any operation (ADR 014, task
 * 438). The loose budget: it stops one runaway integration, and a DynamoDB
 * error admits the request rather than taking `GET /v1/sites` down with it.
 */
export const API_KEY_REQUESTS_PER_MINUTE = 600;
/**
 * Requests a minute a **tenant** may make to the operations that read
 * Tinybird, through the public API and the MCP server together (ADR 014).
 * The budget that matters: Tinybird's limits are the organisation's, shared
 * with production's dashboard, so it is per tenant rather than per key (20
 * keys do not buy 20 times the share) and a DynamoDB error refuses the
 * request. An operator raises it for one tenant on an Enterprise contract,
 * through the `apiRequestsPerMinute` limit override (task 345's), and
 * `resolveApiRequestsPerMinute` in `@headerhawk/core` is how it is read.
 */
export const API_TENANT_REQUESTS_PER_MINUTE = 60;
export const API_KEY_REVOKED_VIA = [
    "member",
    "operator",
    "secret_scanning",
];
export const API_USAGE_KINDS = ["api_key", "mcp"];
/** Days a daily usage item is kept, a margin past the days the view reads. */
export const API_USAGE_TTL_DAYS = 35;
/** Days the operator's usage view shows, today included (task 442). */
export const API_USAGE_DAYS_SHOWN = 30;
/** The UTC day of `ms`, as `YYYY-MM-DD`. */
export function apiUsageDay(ms) {
    return new Date(ms).toISOString().slice(0, 10);
}
/**
 * The partition key of one tenant's calls with one kind of credential on one
 * UTC day, on the public rate-limit table. The public API and the MCP server
 * write it; the admin API reads it.
 */
export function apiUsageKey(tenantId, kind, day) {
    return `USAGE#${tenantId}#${kind}#${day}`;
}
/** The last `count` UTC days ending with `now`'s, oldest first. */
export function apiUsageDays(now, count = API_USAGE_DAYS_SHOWN) {
    const today = Date.UTC(new Date(now).getUTCFullYear(), new Date(now).getUTCMonth(), new Date(now).getUTCDate());
    return Array.from({ length: count }, (_, i) => apiUsageDay(today - (count - 1 - i) * 86_400_000));
}
//# sourceMappingURL=rules.js.map